import { useEffect, useMemo, useRef, useState } from 'react'
import GiftToast from '../../components/gifts/GiftToast'
import { uploadPeopleImage } from '../../api/uploads'
import {
  createPixePhotoAlbum,
  deletePixePhotoAlbum,
  getPixePhotos,
  patchPixePhoto,
  patchPixePhotoAlbum,
  registerPixePhoto,
  reorderPixePhotos,
  setPixePhotosEnabled,
  deletePixePhoto,
} from '../../api/pixePhotos'
import Lightbox, { type LightboxPhoto } from '../../components/Lightbox'
import { resolveMediaUrl } from '../../lib/mediaUrl'
import { getPixePhotoPreviewUrl } from './pixePhotosPreview'

type ToastState = {
  visible: boolean
  emoji: string
  label: string
  detail?: string
  kind?: 'success' | 'error'
}

export type PixePhotoStatus = 'local' | 'uploading' | 'ready' | 'error'

export type PixePhoto = {
  id: string
  albumId?: string | null
  previewUrl: string
  storageKey?: string
  imageUrl?: string
  thumbUrl512?: string
  caption?: string
  sortOrder: number
  isFeatured?: boolean
  status: PixePhotoStatus
  progress?: number
  stage?: 'uploading' | 'registering'
  error?: string
}

const MAX_PHOTOS = 24
const MAX_BYTES = 8 * 1024 * 1024
const ACCEPT_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp'])

function newLocalId(prefix: string) {
  const rand = Math.random().toString(16).slice(2)
  return `${prefix}_${Date.now().toString(16)}_${rand}`
}

function formatBytes(n: number) {
  if (!Number.isFinite(n) || n <= 0) return '0B'
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let v = n
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i += 1
  }
  const digits = i === 0 ? 0 : 1
  return `${v.toFixed(digits)} ${units[i]}`
}

function isBlobUrl(url: string) {
  return (url || '').startsWith('blob:')
}

function normalizePhotoPreviewUrl(url?: string | null): string {
  const raw = String(url || '').trim()
  if (!raw) return ''
  if (isBlobUrl(raw)) return raw
  const resolved = resolveMediaUrl(raw)
  if (resolved) return resolved
  const apiIndex = raw.indexOf('/api/')
  if (apiIndex >= 0) {
    const apiPath = raw.slice(apiIndex)
    return resolveMediaUrl(apiPath) || apiPath
  }
  return raw
}

function arrayMove<T>(arr: T[], from: number, to: number): T[] {
  if (from === to) return arr
  const next = arr.slice()
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

function sortedPhotos(list: PixePhoto[]) {
  const copy = list.slice()
  copy.sort((a, b) => {
    const af = a.isFeatured ? 1 : 0
    const bf = b.isFeatured ? 1 : 0
    if (af !== bf) return bf - af
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  })
  return copy
}

function Card({
  title,
  right,
  children,
}: {
  title: string
  right?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-5 text-white">
      <header className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-sm font-semibold">{title}</h2>
        {right ? <div className="shrink-0">{right}</div> : null}
      </header>
      {children}
    </section>
  )
}

function Badge({ tone, children }: { tone: 'live' | 'disabled'; children: React.ReactNode }) {
  const cls =
    tone === 'live'
      ? 'bg-emerald-500/15 border-emerald-400/20 text-emerald-100'
      : 'bg-white/5 border-white/10 text-white/70'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${cls}`}>
      {children}
    </span>
  )
}

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex h-8 w-14 items-center rounded-full border transition-colors',
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
        checked ? 'bg-sky-500/35 border-sky-400/30' : 'bg-white/5 border-white/15 hover:bg-white/10',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-6 w-6 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-7' : 'translate-x-1',
        ].join(' ')}
      />
    </button>
  )
}

export default function PixePhotosSettings() {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const photosRef = useRef<PixePhoto[]>([])

  const [toast, setToast] = useState<ToastState>({ visible: false, emoji: 'OK', label: '' })
  const showToast = (args: Omit<ToastState, 'visible'> & { visible?: boolean }) =>
    setToast({ visible: true, ...args })

  const [enabled, setEnabled] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [reorderBusy, setReorderBusy] = useState(false)
  const [comingSoonOpen, setComingSoonOpen] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [photos, setPhotos] = useState<PixePhoto[]>([])
  const [albums, setAlbums] = useState<Array<{ id: string; title: string; visibility: string; sort_order: number; price_pixe?: number; is_default?: boolean }>>([])
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | 'all'>('all')
  const [albumCreateOpen, setAlbumCreateOpen] = useState(false)
  const [albumCreateTitle, setAlbumCreateTitle] = useState('')
  const [albumCreateVisibility, setAlbumCreateVisibility] = useState<'public' | 'subscribers' | 'ppv'>('public')
  const [albumBusy, setAlbumBusy] = useState(false)
  const [dragId, setDragId] = useState<string | null>(null)
  const dragStartOrderRef = useRef<string[] | null>(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [captionEditId, setCaptionEditId] = useState<string | null>(null)
  const [captionDraft, setCaptionDraft] = useState('')
  const [captionBusy, setCaptionBusy] = useState(false)
  const [fallbackToImageById, setFallbackToImageById] = useState<Record<string, boolean>>({})

  // Load real state from backend.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const data = await getPixePhotos()
        if (cancelled) return
        setEnabled(Boolean(data.photos_enabled))
        const albumRows = (data.albums || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        setAlbums(albumRows as any)
        const def = albumRows.find((a) => a.is_default) || albumRows[0] || null
        setSelectedAlbumId(def?.id || 'all')
        setPhotos(
          sortedPhotos(
            (data.photos || [])
              .slice(0, MAX_PHOTOS)
              .map((p) => ({
                id: p.id,
                albumId: (p as any).album_id ?? null,
                previewUrl: normalizePhotoPreviewUrl(p.thumb_url_512 || p.image_url),
                imageUrl: normalizePhotoPreviewUrl(p.image_url),
                thumbUrl512: normalizePhotoPreviewUrl(p.thumb_url_512) || undefined,
                caption: p.caption || '',
                sortOrder: Number(p.sort_order ?? 0),
                isFeatured: Boolean(p.is_featured),
                status: 'ready' as const,
                progress: 100,
              }))
          )
        )
      } catch (e: any) {
        if (!cancelled) {
          showToast({
            emoji: '!',
            label: 'Failed to load photos',
            detail: e?.response?.data?.detail || e?.message || 'Please try refresh',
            kind: 'error',
          })
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    photosRef.current = photos
  }, [photos])

  // Cleanup object URLs on unmount.
  useEffect(() => {
    return () => {
      for (const p of photos) {
        if (p.previewUrl && isBlobUrl(p.previewUrl)) URL.revokeObjectURL(p.previewUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const canAddMore = photos.length < MAX_PHOTOS
  const photoCountLabel = `${photos.length} / ${MAX_PHOTOS}`

  useEffect(() => {
    setFallbackToImageById((prev) => {
      const validIds = new Set(photos.map((p) => p.id))
      const next: Record<string, boolean> = {}
      for (const [id, v] of Object.entries(prev)) {
        if (validIds.has(id) && v) next[id] = true
      }
      return next
    })
  }, [photos])

  const defaultAlbumId = useMemo(() => {
    const def = albums.find((a) => a.is_default)
    return def?.id || (albums[0]?.id ?? null)
  }, [albums])

  const selectedAlbum = useMemo(() => {
    if (selectedAlbumId === 'all') return null
    return albums.find((a) => a.id === selectedAlbumId) || null
  }, [albums, selectedAlbumId])

  const nextSortOrder = useMemo(() => {
    if (!photos.length) return 0
    const max = photos.reduce((m, p) => Math.max(m, Number(p.sortOrder ?? 0)), 0)
    return max + 1
  }, [photos])

  const visiblePhotos = useMemo(() => {
    if (selectedAlbumId === 'all') return photos
    return photos.filter((p) => (p.albumId || null) === selectedAlbumId)
  }, [photos, selectedAlbumId])

  const renderPhotos = useMemo(() => sortedPhotos(visiblePhotos), [visiblePhotos])
  const orderedIds = useMemo(() => renderPhotos.map((p) => p.id), [renderPhotos])

  const validateFiles = (files: File[]) => {
    const accepted: File[] = []
    const rejected: { file: File; reason: string }[] = []
    for (const f of files) {
      if (!ACCEPT_MIMES.has(f.type)) {
        rejected.push({ file: f, reason: `Unsupported type (${f.type || 'unknown'})` })
        continue
      }
      if (f.size > MAX_BYTES) {
        rejected.push({ file: f, reason: `Too large (${formatBytes(f.size)}; max ${formatBytes(MAX_BYTES)})` })
        continue
      }
      accepted.push(f)
    }
    return { accepted, rejected }
  }

  const addFiles = async (filesLike: FileList | File[]) => {
    const raw = Array.from(filesLike || [])
    if (raw.length === 0) return

    if (!canAddMore) {
      showToast({ emoji: '!', label: 'Photo limit reached', detail: `Max ${MAX_PHOTOS} photos.` , kind: 'error' })
      return
    }

    const { accepted, rejected } = validateFiles(raw)
    if (rejected.length) {
      const first = rejected[0]
      showToast({
        emoji: '!',
        label: 'Some files were skipped',
        detail: `${first.file.name}: ${first.reason}`,
        kind: 'error',
      })
    }

    const space = MAX_PHOTOS - photos.length
    const toAdd = accepted.slice(0, Math.max(0, space))
    if (toAdd.length === 0) return
    if (accepted.length > toAdd.length) {
      showToast({ emoji: '!', label: 'Photo limit reached', detail: `Added ${toAdd.length}. Max ${MAX_PHOTOS}.`, kind: 'error' })
    }

    // Add placeholders immediately (optimistic), then upload/register each.
    const locals: Array<{ localId: string; file: File; previewUrl: string; sortOrder: number; albumId: string | null }> = []
    const startSort = nextSortOrder
    for (let i = 0; i < toAdd.length; i++) {
      const f = toAdd[i]
      locals.push({
        localId: newLocalId('photo'),
        file: f,
        previewUrl: URL.createObjectURL(f),
        sortOrder: startSort + i,
        albumId: selectedAlbumId === 'all' ? null : selectedAlbumId,
      })
    }
    setPhotos((prev) => [
      ...prev,
      ...locals.map((l) => ({
        id: l.localId,
        albumId: l.albumId,
        previewUrl: l.previewUrl,
        caption: '',
        sortOrder: l.sortOrder,
        isFeatured: false,
        status: 'uploading' as const,
        stage: 'uploading' as const,
        progress: 0,
      })),
    ])

    // Upload & register sequentially to avoid spiky load; keeps UI simple and predictable.
    for (const l of locals) {
      try {
        // 1) Upload binary
        const up = await uploadPeopleImage(l.file, {
          onProgress: (pct) => {
            // Keep a little headroom for the register step.
            const clamped = Math.max(0, Math.min(95, pct))
            setPhotos((prev) =>
              prev.map((p) =>
                p.id === l.localId ? { ...p, progress: clamped, status: 'uploading', stage: 'uploading' } : p
              )
            )
          },
        })

        // 2) Register gallery row
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === l.localId ? { ...p, storageKey: up.key, stage: 'registering', progress: 96 } : p
          )
        )
        const created = await registerPixePhoto({
          storage_key: up.key,
          thumb_key_512: up.thumb_key_512,
          caption: '',
          sort_order: l.sortOrder,
          is_public: true,
          album_id: l.albumId,
          width: up.width ?? null,
          height: up.height ?? null,
          bytes: up.bytes ?? null,
          mime: up.mime || '',
        })

        // Swap local id -> real id, replace blob preview with CDN url (revoke blob).
        setPhotos((prev) =>
          prev.map((p) => {
            if (p.id !== l.localId) return p
            if (p.previewUrl && isBlobUrl(p.previewUrl)) URL.revokeObjectURL(p.previewUrl)
            const imageUrl = normalizePhotoPreviewUrl(created.image_url || up.url)
            const thumbUrl512 = normalizePhotoPreviewUrl(created.thumb_url_512 || up.thumb_url_512)
            return {
              ...p,
              id: created.id,
              albumId: (created as any).album_id ?? l.albumId,
              previewUrl: thumbUrl512 || imageUrl || normalizePhotoPreviewUrl(up.url),
              imageUrl,
              thumbUrl512: thumbUrl512 || undefined,
              caption: created.caption || '',
              sortOrder: Number(created.sort_order ?? l.sortOrder),
              isFeatured: Boolean(created.is_featured),
              status: 'ready' as const,
              stage: undefined,
              progress: 100,
              error: undefined,
            }
          })
        )
      } catch (e: any) {
        const msg = e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Upload failed'
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === l.localId ? { ...p, status: 'error', stage: undefined, error: msg, progress: 0 } : p
          )
        )
        showToast({ emoji: '!', label: 'Upload failed', detail: msg, kind: 'error' })
      }
    }
  }

  const onDelete = (id: string) => {
    const snapshot = photos
    setPhotos((prev) => {
      const found = prev.find((p) => p.id === id)
      if (found?.previewUrl && isBlobUrl(found.previewUrl)) URL.revokeObjectURL(found.previewUrl)
      return prev.filter((p) => p.id !== id)
    })
    ;(async () => {
      try {
        // If it is a local placeholder (not yet registered), nothing to delete server-side.
        if (id.startsWith('photo_')) return
        await deletePixePhoto(id)
        showToast({ emoji: 'OK', label: 'Removed photo', kind: 'success' })
      } catch (e: any) {
        // Roll back on failure
        setPhotos(snapshot)
        showToast({
          emoji: '!',
          label: 'Delete failed',
          detail: e?.response?.data?.detail || e?.message || 'Please try again',
          kind: 'error',
        })
      }
    })()
  }

  const persistReorder = async (ids: string[]) => {
    setReorderBusy(true)
    try {
      const scopeAlbumId = selectedAlbumId === 'all' ? null : selectedAlbumId
      const data = await reorderPixePhotos(ids, scopeAlbumId)
      const albumRows = (data.albums || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      setAlbums(albumRows as any)
      setPhotos(
        sortedPhotos((data.photos || []).slice(0, MAX_PHOTOS).map((p) => ({
          id: p.id,
          albumId: (p as any).album_id ?? null,
          previewUrl: normalizePhotoPreviewUrl(p.thumb_url_512 || p.image_url),
          imageUrl: normalizePhotoPreviewUrl(p.image_url),
          thumbUrl512: normalizePhotoPreviewUrl(p.thumb_url_512) || undefined,
          caption: p.caption || '',
          sortOrder: Number(p.sort_order ?? 0),
          isFeatured: Boolean(p.is_featured),
          status: 'ready' as const,
          progress: 100,
        })))
      )
    } catch (e: any) {
      showToast({
        emoji: '!',
        label: 'Reorder failed',
        detail: e?.response?.data?.detail || e?.message || 'Please try again',
        kind: 'error',
      })
    } finally {
      setReorderBusy(false)
    }
  }

  const moveBy = (id: string, delta: number) => {
    if (selectedAlbumId === 'all') return
    const current = visiblePhotos
    const idx = current.findIndex((p) => p.id === id)
    if (idx < 0) return
    const to = Math.max(0, Math.min(current.length - 1, idx + delta))
    if (to === idx) return
    const nextIds = arrayMove(current.map((p) => p.id), idx, to)
    // Optimistically reorder within the visible scope by updating sort_order in-memory.
    setPhotos((prev) =>
      prev.map((p) => {
        const scoped = nextIds.includes(p.id)
        if (!scoped) return p
        return { ...p, sortOrder: nextIds.indexOf(p.id) }
      })
    )
    void persistReorder(nextIds)
  }

  const setFeatured = async (id: string, next: boolean) => {
    const prev = photos
    setPhotos((p) =>
      p.map((x) => (x.id === id ? { ...x, isFeatured: next } : { ...x, isFeatured: next ? false : x.isFeatured }))
    )
    try {
      await patchPixePhoto(id, { is_featured: next })
      // Keep local invariant: only one featured; also put featured first visually.
      setPhotos((p) => sortedPhotos(p))
    } catch (e: any) {
      setPhotos(prev)
      showToast({
        emoji: '!',
        label: 'Featured update failed',
        detail: e?.response?.data?.detail || e?.message || 'Please try again',
        kind: 'error',
      })
    }
  }

  const beginEditCaption = (id: string) => {
    const p = photos.find((x) => x.id === id)
    if (!p) return
    setCaptionEditId(id)
    setCaptionDraft(p.caption || '')
  }

  const saveCaption = async () => {
    if (!captionEditId) return
    const id = captionEditId
    const next = (captionDraft || '').slice(0, 140)
    setCaptionBusy(true)
    const prev = photos
    setPhotos((p) => p.map((x) => (x.id === id ? { ...x, caption: next } : x)))
    try {
      await patchPixePhoto(id, { caption: next })
      setCaptionEditId(null)
      showToast({ emoji: 'OK', label: 'Caption saved', kind: 'success' })
    } catch (e: any) {
      setPhotos(prev)
      showToast({
        emoji: '!',
        label: 'Caption save failed',
        detail: e?.response?.data?.detail || e?.message || 'Please try again',
        kind: 'error',
      })
    } finally {
      setCaptionBusy(false)
    }
  }

  const onPickFiles = () => fileInputRef.current?.click()

  const createAlbum = async () => {
    const title = (albumCreateTitle || '').trim()
    if (!title) {
      showToast({ emoji: '!', label: 'Album title required', detail: 'Enter a name like "Behind the scenes".', kind: 'error' })
      return
    }
    if (title.length > 80) {
      showToast({ emoji: '!', label: 'Title too long', detail: 'Keep album titles under 80 characters.', kind: 'error' })
      return
    }
    setAlbumBusy(true)
    try {
      const created = await createPixePhotoAlbum({ title, visibility: albumCreateVisibility })
      setAlbumCreateOpen(false)
      setAlbumCreateTitle('')
      setAlbumCreateVisibility('public')
      setSelectedAlbumId(created.id)
      await refresh()
      showToast({ emoji: 'OK', label: 'Album created', kind: 'success' })
    } catch (e: any) {
      showToast({ emoji: '!', label: 'Create failed', detail: e?.response?.data?.detail || e?.message || 'Please try again', kind: 'error' })
    } finally {
      setAlbumBusy(false)
    }
  }

  const movePhotoToAlbum = async (photoId: string, albumId: string | null) => {
    // Optimistic update
    const prev = photos
    setPhotos((p) => p.map((x) => (x.id === photoId ? { ...x, albumId } : x)))
    try {
      if (photoId.startsWith('photo_')) return
      const updated = await patchPixePhoto(photoId, { album_id: albumId })
      setPhotos((p) =>
        p.map((x) =>
          x.id === updated.id
            ? {
                ...x,
                albumId: (updated as any).album_id ?? albumId,
                sortOrder: Number(updated.sort_order ?? x.sortOrder),
              }
            : x
        )
      )
      showToast({ emoji: 'OK', label: 'Moved', kind: 'success' })
    } catch (e: any) {
      setPhotos(prev)
      showToast({ emoji: '!', label: 'Move failed', detail: e?.response?.data?.detail || e?.message || 'Please try again', kind: 'error' })
    }
  }

  const onToggleEnabled = async (next: boolean) => {
    if (saving) return
    const prev = enabled
    setEnabled(next)
    setSaving(true)
    try {
      const data = await setPixePhotosEnabled(next)
      setEnabled(Boolean(data.photos_enabled))
      showToast({
        emoji: 'OK',
        label: next ? 'Photos are live' : 'Photos disabled',
        detail: next ? 'Your People profile can now show a Photos section.' : 'Your profile will not show Photos.',
        kind: 'success',
      })
    } catch (e: any) {
      setEnabled(prev)
      showToast({
        emoji: '!',
        label: 'Save failed',
        detail: e?.response?.data?.detail || e?.message || 'Please try again',
        kind: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const galleryHint = useMemo(() => {
    return enabled
      ? 'Tip: add a few photos first, then switch your profile to Live.'
      : 'Add photos now, then turn on visibility when you are ready.'
  }, [enabled])

  const refresh = async () => {
    setLoading(true)
    try {
      const data = await getPixePhotos()
      setEnabled(Boolean(data.photos_enabled))
      const albumRows = (data.albums || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      setAlbums(albumRows as any)
      const def = albumRows.find((a) => a.is_default) || albumRows[0] || null
      if (selectedAlbumId !== 'all' && def && !albumRows.find((a) => a.id === selectedAlbumId)) {
        setSelectedAlbumId(def.id)
      }
      setPhotos(
        sortedPhotos((data.photos || []).slice(0, MAX_PHOTOS).map((p) => ({
          id: p.id,
          albumId: (p as any).album_id ?? null,
          previewUrl: normalizePhotoPreviewUrl(p.thumb_url_512 || p.image_url),
          imageUrl: normalizePhotoPreviewUrl(p.image_url),
          thumbUrl512: normalizePhotoPreviewUrl(p.thumb_url_512) || undefined,
          caption: p.caption || '',
          sortOrder: Number(p.sort_order ?? 0),
          isFeatured: Boolean(p.is_featured),
          status: 'ready' as const,
          progress: 100,
        })))
      )
    } catch (e: any) {
      showToast({ emoji: '!', label: 'Refresh failed', detail: e?.message || 'Please try again', kind: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const openLightbox = (idx: number) => {
    setLightboxIndex(Math.max(0, Math.min(renderPhotos.length - 1, idx)))
    setLightboxOpen(true)
  }

  const lightboxPhotos = useMemo(() => {
    return renderPhotos.map((p) => ({
      id: p.id,
      image_url: p.imageUrl || p.previewUrl,
      caption: p.caption || '',
      is_featured: Boolean(p.isFeatured),
    })) as LightboxPhoto[]
  }, [renderPhotos])

  return (
    <div className="text-white">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-extrabold">Pixe Photos</h1>
          <p className="text-sm text-white/70 mt-1">
            Build a lightweight photo gallery on your People profile.
          </p>
        </div>
        <div className="pt-1">
          <Badge tone={enabled ? 'live' : 'disabled'}>{enabled ? 'Live' : 'Disabled'}</Badge>
        </div>
      </div>

      <div className="space-y-5">
        <Card
          title="Profile Visibility"
          right={<Toggle checked={enabled} disabled={saving} onChange={onToggleEnabled} />}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm text-white/85">
                Show Photos on my profile
              </div>
              <div className="text-xs text-white/60 mt-1">
                When enabled, a Photos section will appear on your public People page.
              </div>
              {!enabled ? (
                <div className="mt-3 rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-white/70">
                  Your profile does not currently show Photos.
                </div>
              ) : (
                <div className="mt-3 rounded-xl border border-emerald-400/15 bg-emerald-500/10 p-3 text-xs text-emerald-100/90">
                  Photos are live on your profile.
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 text-xs text-white/55">{saving ? 'Saving…' : 'Change anytime. No lock-in.'}</div>
        </Card>

        <Card
          title="Gallery"
          right={
            <div className="flex items-center gap-3">
              <span className="text-xs text-white/60">{photoCountLabel}</span>
              <button
                type="button"
                onClick={onPickFiles}
                disabled={!canAddMore || loading}
                className={[
                  'px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors',
                  canAddMore && !loading
                    ? 'border-white/15 bg-white/5 hover:bg-white/10'
                    : 'border-white/10 bg-white/5 text-white/45 cursor-not-allowed',
                ].join(' ')}
              >
                Upload Photos
              </button>
            </div>
          }
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files || [])
              if (e.currentTarget) e.currentTarget.value = ''
            }}
          />

          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <label className="text-xs text-white/65">Album</label>
              <select
                value={selectedAlbumId}
                onChange={(e) => setSelectedAlbumId(e.target.value as any)}
                className="px-2 py-1.5 rounded-lg border border-white/15 bg-black/40 text-sm text-white"
                // Hint to browsers that support it to render the native dropdown menu in dark mode.
                style={{ colorScheme: 'dark' }}
              >
                <option value="all" className="bg-black text-white">
                  All photos
                </option>
                {albums.map((a) => (
                  <option key={a.id} value={a.id} className="bg-black text-white">
                    {a.title}{a.is_default ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setAlbumCreateOpen(true)}
                className="px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-sm font-semibold"
              >
                New album
              </button>
              <button
                type="button"
                onClick={() => void refresh()}
                disabled={loading}
                className="px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-sm font-semibold disabled:opacity-50"
              >
                Refresh
              </button>
            </div>
            {selectedAlbum ? (
              <div className="flex items-center gap-2">
                <label className="text-xs text-white/65">Visibility</label>
                <select
                  value={selectedAlbum.visibility}
                  onChange={async (e) => {
                    const v = e.target.value as any
                    setAlbumBusy(true)
                    try {
                      const updated = await patchPixePhotoAlbum(selectedAlbum.id, { visibility: v })
                      setAlbums((prev) => prev.map((a) => (a.id === updated.id ? { ...a, visibility: updated.visibility } : a)))
                      showToast({ emoji: 'OK', label: 'Album updated', kind: 'success' })
                    } catch (err: any) {
                      showToast({ emoji: '!', label: 'Album update failed', detail: err?.response?.data?.detail || err?.message, kind: 'error' })
                    } finally {
                      setAlbumBusy(false)
                    }
                  }}
                  disabled={albumBusy}
                  className="px-2 py-1.5 rounded-lg border border-white/15 bg-black/40 text-sm text-white disabled:opacity-60"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="public" className="bg-black text-white">
                    Public
                  </option>
                  <option value="subscribers" className="bg-black text-white">
                    Subscribers
                  </option>
                  <option value="ppv" className="bg-black text-white">
                    PPV (coming soon)
                  </option>
                </select>
                {String(selectedAlbum.visibility || '') === 'ppv' ? (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-white/65">PPV price</label>
                    <input
                      type="number"
                      min={1}
                      max={9999}
                      step={1}
                      value={Number((selectedAlbum as any).price_pixe ?? 10)}
                      onChange={(e) => {
                        const raw = e.target.value
                        const next = raw === '' ? 10 : Math.max(1, Math.min(9999, Number(raw) || 10))
                        setAlbums((prev) => prev.map((a) => (a.id === selectedAlbum.id ? { ...a, price_pixe: next } : a)))
                      }}
                      onBlur={async (e) => {
                        const v = Math.max(1, Math.min(9999, Number(e.target.value) || 10))
                        setAlbumBusy(true)
                        try {
                          const updated = await patchPixePhotoAlbum(selectedAlbum.id, { price_pixe: v })
                          setAlbums((prev) => prev.map((a) => (a.id === updated.id ? { ...a, price_pixe: (updated as any).price_pixe } : a)))
                          showToast({ emoji: 'OK', label: 'Album updated', kind: 'success' })
                        } catch (err: any) {
                          showToast({ emoji: '!', label: 'Album update failed', detail: err?.response?.data?.detail || err?.message, kind: 'error' })
                        } finally {
                          setAlbumBusy(false)
                        }
                      }}
                      disabled={albumBusy}
                      className="w-28 px-2 py-1.5 rounded-lg border border-white/15 bg-black/40 text-sm text-white disabled:opacity-60"
                    />
                  </div>
                ) : null}
                {!selectedAlbum.is_default ? (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!confirm('Delete this album? Photos will be moved to your default album.')) return
                      setAlbumBusy(true)
                      try {
                        await deletePixePhotoAlbum(selectedAlbum.id)
                        showToast({ emoji: 'OK', label: 'Album deleted', kind: 'success' })
                        setSelectedAlbumId(defaultAlbumId || 'all')
                        await refresh()
                      } catch (err: any) {
                        showToast({ emoji: '!', label: 'Delete failed', detail: err?.response?.data?.detail || err?.message, kind: 'error' })
                      } finally {
                        setAlbumBusy(false)
                      }
                    }}
                    disabled={albumBusy}
                    className="px-3 py-1.5 rounded-lg border border-red-500/30 bg-red-500/10 hover:bg-red-500/15 text-sm font-semibold disabled:opacity-50"
                  >
                    Delete album
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="text-xs text-white/60 -mt-1 mb-3">
            JPG, PNG, or WebP. Up to {formatBytes(MAX_BYTES)} each. Max {MAX_PHOTOS} photos.
          </div>

          <div
            className={[
              'rounded-2xl border border-dashed p-5 transition-colors',
              dragActive ? 'border-sky-300/50 bg-sky-500/10' : 'border-white/15 bg-black/20 hover:bg-black/25',
            ].join(' ')}
            onDragEnter={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDragActive(true)
            }}
            onDragOver={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDragActive(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDragActive(false)
            }}
            onDrop={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDragActive(false)
              const files = e.dataTransfer?.files
              if (files) void addFiles(files)
            }}
          >
            {loading ? (
              <div className="space-y-3">
                <div className="h-4 w-40 rounded bg-white/10" />
                <div className="h-3 w-72 rounded bg-white/10" />
                <div className="h-10 w-36 rounded-xl bg-white/10" />
              </div>
            ) : photos.length === 0 ? (
              <div className="flex flex-col items-center text-center">
                <div className="text-sm font-semibold">Drop photos here</div>
                <div className="text-xs text-white/60 mt-1">
                  Or click to choose files from your computer.
                </div>
                <button
                  type="button"
                  onClick={onPickFiles}
                  className="mt-4 px-4 py-2 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-sm font-semibold"
                >
                  Choose photos
                </button>
                <div className="mt-4 text-xs text-white/55">{galleryHint}</div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
	                  {renderPhotos.map((p, idx) => (
	                    <div
	                      key={p.id}
	                      className="group relative aspect-square rounded-2xl overflow-hidden border border-white/10 bg-black/30"
                      draggable={selectedAlbumId !== 'all' && p.status !== 'uploading' && !reorderBusy && !loading}
                      onDragStart={() => {
                        if (selectedAlbumId === 'all') return
                        setDragId(p.id)
                        dragStartOrderRef.current = orderedIds.slice()
                      }}
                      onDragOver={(e) => {
                        if (selectedAlbumId === 'all') return
                        if (!dragId) return
                        e.preventDefault()
                        const from = orderedIds.findIndex((x) => x === dragId)
                        const to = idx
                        if (from < 0 || to < 0 || from === to) return
                        const nextIds = arrayMove(orderedIds, from, to)
                        // Optimistically update sort_order for this album scope so renderPhotos reflects the new order.
                        setPhotos((prev) =>
                          prev.map((row) => {
                            if (!nextIds.includes(row.id)) return row
                            return { ...row, sortOrder: nextIds.indexOf(row.id) }
                          })
                        )
                      }}
                      onDragEnd={() => {
                        if (selectedAlbumId === 'all') return
                        const start = dragStartOrderRef.current
                        setDragId(null)
                        dragStartOrderRef.current = null
                        if (!start) return
                        const now = sortedPhotos(
                          photosRef.current.filter((x) => (x.albumId || null) === selectedAlbumId)
                        ).map((x) => x.id)
                        if (start.join('|') !== now.join('|')) void persistReorder(now)
                      }}
                    >
                      <button
                        type="button"
                        className="absolute inset-0"
                        onClick={() => openLightbox(idx)}
                        aria-label="Open photo"
                        title="Open"
                      />
                      <img
                        src={getPixePhotoPreviewUrl(p, Boolean(fallbackToImageById[p.id]))}
                        className="h-full w-full object-cover"
                        alt="Photo"
                        loading="lazy"
                        onError={() => {
                          if (p.thumbUrl512 && p.imageUrl) {
                            setFallbackToImageById((prev) => ({ ...prev, [p.id]: true }))
                          }
                        }}
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />

                      <div className="absolute left-2 bottom-2 right-2">
                        {p.status === 'uploading' ? (
                          <div className="rounded-lg border border-white/10 bg-black/40 px-2 py-1">
                            <div className="flex items-center justify-between text-[11px] text-white/80">
                              <span>{p.stage === 'registering' ? 'Finalizing' : 'Uploading'}</span>
                              <span>{Math.round(p.progress ?? 0)}%</span>
                            </div>
                            <div className="mt-1 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                              <div
                                className="h-full bg-sky-400/80"
                                style={{ width: `${Math.max(3, Math.round(p.progress ?? 0))}%` }}
                              />
                            </div>
                          </div>
                        ) : p.status === 'error' ? (
                          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-2 py-1 text-[11px] text-red-100">
                            {p.error || 'Upload failed'}
                          </div>
                        ) : null}
                      </div>

	                      <button
	                        type="button"
	                        onClick={() => onDelete(p.id)}
	                        disabled={p.status === 'uploading'}
                        className={[
                          'absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 rounded-lg border text-xs',
                          p.status === 'uploading'
                            ? 'border-white/10 bg-black/40 text-white/55 cursor-not-allowed'
                            : 'border-white/15 bg-black/50 hover:bg-black/65',
                        ].join(' ')}
                        aria-label="Delete photo"
                        title="Delete"
                      >
	                        Delete
	                      </button>

	                      <div className="absolute top-2 left-2 flex items-center gap-2">
	                        <button
	                          type="button"
	                          onClick={() => void setFeatured(p.id, !p.isFeatured)}
	                          disabled={p.status === 'uploading' || reorderBusy}
                          className={[
                            'opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 rounded-lg border text-xs',
                            p.isFeatured
                              ? 'border-amber-300/30 bg-amber-400/15 text-amber-100'
                              : 'border-white/15 bg-black/50 hover:bg-black/65',
                          ].join(' ')}
                          aria-label={p.isFeatured ? 'Unset featured' : 'Set featured'}
                          title={p.isFeatured ? 'Featured' : 'Set featured'}
	                        >
	                          {p.isFeatured ? 'Featured' : 'Feature'}
	                        </button>
		                        {albums.length ? (
		                          <select
		                            value={p.albumId ?? ''}
		                            onChange={(e) => void movePhotoToAlbum(p.id, e.target.value || null)}
		                            disabled={p.status === 'uploading' || reorderBusy || albumBusy}
		                            className="opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 rounded-lg border border-white/15 bg-black/70 text-xs text-white disabled:opacity-50"
		                            title="Move to album"
		                            aria-label="Move to album"
		                            style={{ colorScheme: 'dark' }}
		                          >
		                            {selectedAlbumId === 'all' ? (
		                              <option value="" className="bg-black text-white">
		                                No album
		                              </option>
		                            ) : null}
		                            {albums.map((a) => (
		                              <option key={a.id} value={a.id} className="bg-black text-white">
		                                {a.title}{a.is_default ? ' (Default)' : ''}
		                              </option>
		                            ))}
		                          </select>
		                        ) : null}
	                      </div>

                      <div className="absolute bottom-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => beginEditCaption(p.id)}
                          disabled={p.status === 'uploading'}
                          className="px-2 py-1 rounded-lg border border-white/15 bg-black/50 hover:bg-black/65 text-xs"
                        >
                          Caption
                        </button>
                        <button
                          type="button"
                          onClick={() => moveBy(p.id, -1)}
                          disabled={selectedAlbumId === 'all' || reorderBusy || p.status === 'uploading'}
                          className="px-2 py-1 rounded-lg border border-white/15 bg-black/50 hover:bg-black/65 text-xs"
                          aria-label="Move left"
                          title="Move left"
                        >
                          ◀
                        </button>
                        <button
                          type="button"
                          onClick={() => moveBy(p.id, +1)}
                          disabled={selectedAlbumId === 'all' || reorderBusy || p.status === 'uploading'}
                          className="px-2 py-1 rounded-lg border border-white/15 bg-black/50 hover:bg-black/65 text-xs"
                          aria-label="Move right"
                          title="Move right"
                        >
                          ▶
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

	                <div className="flex items-center justify-between gap-3">
	                  <div className="text-xs text-white/55">
	                    Tip: keep albums small and focused. Subscriber-only albums show as locked previews on your profile.
	                  </div>
	                  <button
	                    type="button"
	                    onClick={onPickFiles}
                    disabled={!canAddMore || loading}
                    className={[
                      'px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors',
                      canAddMore && !loading
                        ? 'border-white/15 bg-white/5 hover:bg-white/10'
                        : 'border-white/10 bg-white/5 text-white/45 cursor-not-allowed',
                    ].join(' ')}
                  >
                    Add more
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card
          title="Coming Soon"
          right={
            <button
              type="button"
              onClick={() => setComingSoonOpen((v) => !v)}
              className="px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-sm font-semibold"
              aria-expanded={comingSoonOpen}
            >
              {comingSoonOpen ? 'Hide' : 'Show'}
            </button>
          }
        >
          <div className="text-xs text-white/60">
            Premium photo tools will make galleries feel native to subscriptions and commerce.
          </div>
          {comingSoonOpen ? (
            <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
              <ul className="text-sm text-white/80 list-disc pl-5 space-y-2">
                <li>Subscriber-only albums</li>
                <li>Pay-per-view unlocks</li>
                <li>Photo packs</li>
                <li>Anchored authenticity receipts</li>
              </ul>
            </div>
          ) : null}
        </Card>
      </div>

	      {captionEditId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => !captionBusy && setCaptionEditId(null)} />
          <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-neutral-950 p-5 text-white shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-semibold">Edit caption</div>
                <div className="text-xs text-white/60 mt-1">Max 140 characters.</div>
              </div>
              <button
                type="button"
                onClick={() => setCaptionEditId(null)}
                disabled={captionBusy}
                className="px-2 py-1 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs"
              >
                Close
              </button>
            </div>
            <textarea
              value={captionDraft}
              onChange={(e) => setCaptionDraft(e.target.value.slice(0, 140))}
              rows={3}
              className="mt-4 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-400/40"
              placeholder="Add a short caption…"
            />
            <div className="mt-2 flex items-center justify-between text-xs text-white/55">
              <div>{captionDraft.length} / 140</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCaptionEditId(null)}
                  disabled={captionBusy}
                  className="px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void saveCaption()}
                  disabled={captionBusy}
                  className="px-3 py-1.5 rounded-lg border border-sky-400/30 bg-sky-500/20 hover:bg-sky-500/30 text-xs font-semibold"
                >
                  {captionBusy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
	      ) : null}

	      {albumCreateOpen ? (
	        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
	          <div className="absolute inset-0 bg-black/70" onClick={() => !albumBusy && setAlbumCreateOpen(false)} />
	          <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-neutral-950 p-5 text-white shadow-xl">
	            <div className="flex items-start justify-between gap-4">
	              <div>
	                <div className="text-sm font-semibold">New album</div>
	                <div className="text-xs text-white/60 mt-1">Albums help you group photos and control visibility.</div>
	              </div>
	              <button
	                type="button"
	                onClick={() => setAlbumCreateOpen(false)}
	                disabled={albumBusy}
	                className="px-2 py-1 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs"
	              >
	                Close
	              </button>
	            </div>
	            <div className="mt-4 space-y-3">
	              <div>
	                <label className="block text-xs text-white/65">Title</label>
	                <input
	                  value={albumCreateTitle}
	                  onChange={(e) => setAlbumCreateTitle(e.target.value.slice(0, 80))}
	                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-400/40"
	                  placeholder='e.g. "Behind the scenes"'
	                  autoFocus
	                />
	                <div className="mt-1 text-[11px] text-white/55">{albumCreateTitle.trim().length} / 80</div>
	              </div>
		              <div>
		                <label className="block text-xs text-white/65">Visibility</label>
		                <select
		                  value={albumCreateVisibility}
		                  onChange={(e) => setAlbumCreateVisibility(e.target.value as any)}
		                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-sky-400/40"
		                  style={{ colorScheme: 'dark' }}
		                >
		                  <option value="public" className="bg-black text-white">
		                    Public
		                  </option>
		                  <option value="subscribers" className="bg-black text-white">
		                    Subscribers
		                  </option>
		                  <option value="ppv" className="bg-black text-white">
		                    PPV (coming soon)
		                  </option>
		                </select>
		                {albumCreateVisibility === 'ppv' ? (
		                  <div className="mt-1 text-[11px] text-white/55">PPV albums will show as locked for now.</div>
		                ) : null}
		              </div>
	              <div className="flex items-center justify-end gap-2 pt-1">
	                <button
	                  type="button"
	                  onClick={() => setAlbumCreateOpen(false)}
	                  disabled={albumBusy}
	                  className="px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-semibold disabled:opacity-50"
	                >
	                  Cancel
	                </button>
	                <button
	                  type="button"
	                  onClick={() => void createAlbum()}
	                  disabled={albumBusy}
	                  className="px-3 py-1.5 rounded-lg border border-sky-400/30 bg-sky-500/20 hover:bg-sky-500/30 text-xs font-semibold disabled:opacity-50"
	                >
	                  {albumBusy ? 'Creating...' : 'Create album'}
	                </button>
	              </div>
	            </div>
	          </div>
	        </div>
	      ) : null}

	      <Lightbox
	        isOpen={lightboxOpen}
	        photos={lightboxPhotos}
	        startIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />

      <GiftToast
        visible={toast.visible}
        emoji={toast.emoji}
        label={toast.label}
        detail={toast.detail}
        kind={toast.kind}
        onHide={() => setToast((t) => ({ ...t, visible: false }))}
        timeoutMs={2200}
      />
    </div>
  )
}
